import { Controller } from "@hotwired/stimulus"

// Drives the Astro cart page: submits forms on input change (qty steppers, country),
// opens/closes the signs modal, and smooth-scrolls to the payment step.
// No client-side cart state — the server re-renders items + recap via Turbo Stream.
export default class extends Controller {
  static targets = ["modal", "autovisit"]

  connect() {
    this.hideStickyOnKeyboard()
  }

  disconnect() {
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.viewportHandler)
    }
  }

  // iOS pins position:fixed elements above the virtual keyboard, so the bottom sticky CTA
  // floats into the middle of the form while the customer types (e.g. the phone field).
  // Hide it whenever the keyboard is open (same fix used on the Calendrier/LSDM tunnels).
  hideStickyOnKeyboard() {
    if (!window.visualViewport) return

    const threshold = 150
    this.viewportHandler = () => {
      const sticky = this.element.querySelector(".mobile-sticky")
      if (!sticky) return
      const keyboardOpen = window.innerHeight - window.visualViewport.height > threshold
      sticky.style.display = keyboardOpen ? "none" : ""
    }
    window.visualViewport.addEventListener("resize", this.viewportHandler)
  }

  // When the payment frame loads an already-paid cart, it renders a top-level link
  // (autovisit target) to the confirmation page — follow it automatically.
  autovisitTargetConnected(el) {
    el.click()
  }

  // Submit the closest form via requestSubmit() so Turbo intercepts it as a Stream
  // request (the app runs with Turbo Drive disabled globally; requestSubmit still goes
  // through Turbo's form observer, a native submit-button click may not).
  submitForm(event) {
    const form = event.target.closest("form")
    if (!form) return
    event.preventDefault()
    form.requestSubmit()
  }

  openModal() {
    if (this.hasModalTarget) this.modalTarget.classList.add("open")
  }

  closeModal() {
    if (this.hasModalTarget) this.modalTarget.classList.remove("open")
  }

  closeModalOnBackdrop(event) {
    if (event.target === this.modalTarget) this.modalTarget.classList.remove("open")
  }

  // The recap / sticky CTA. On desktop the payment step has no visible submit button
  // (mockup parity), so this CTA both reveals the payment step and triggers the Stripe
  // submit. The submit button still exists inside the frame (hidden on desktop) and owns
  // all the Stripe logic — we just click it once the frame is mounted.
  //
  // The PaymentIntent's amount is synced in place on every cart edit (server-side), so the
  // mounted Stripe form is ALWAYS valid — we never reload the frame here (that would wipe
  // what the customer typed). If the form is mounted, submit it; if it's still loading
  // (rebuilt after a country change), submit once it loads.
  scrollToPayment() {
    const frame = document.getElementById("astro-payment-step")
    if (!frame) return

    // Guard against repeated clicks: a submit in flight disables the step's submit button.
    // Don't fire another one (would double-submit / double-charge). astro-payment#submit
    // re-enables the button on validation/Stripe error, so retries still work.
    const mountedButton = frame.querySelector("[data-astro-payment-target='submitButton']")
    if (mountedButton && mountedButton.disabled) return

    frame.scrollIntoView({ behavior: "smooth", block: "start" })

    if (mountedButton) {
      this.submitPaymentWhenReady(frame)
    } else {
      // Frame not mounted yet (e.g. just rebuilt): submit once it finishes loading.
      const onLoad = (event) => {
        if (event.target.id !== "astro-payment-step") return
        document.removeEventListener("turbo:frame-load", onLoad)
        this.submitPaymentWhenReady(frame)
      }
      document.addEventListener("turbo:frame-load", onLoad)
    }
  }

  // The payment frame mounts Stripe asynchronously. Poll briefly for the submit button,
  // then click it (its data-action runs astro-payment#submit). Skip if it's already
  // disabled (a submit is in flight). Give up after ~5s so a stuck frame doesn't hang.
  submitPaymentWhenReady(frame, attempts = 0) {
    const button = frame.querySelector("[data-astro-payment-target='submitButton']")
    if (button) {
      if (!button.disabled) button.click()
      return
    }
    if (attempts >= 50) return
    setTimeout(() => this.submitPaymentWhenReady(frame, attempts + 1), 100)
  }
}
